import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axiosInstance from '../../hooks/axios';
import PageBreadcrumb from '../../components/common/PageBreadCrumb';

interface Customer {
  id: string;
  fullName: string;
  phoneNumber: string;
  email: string;
  profilePictureUrl?: string;
  languagePreference?: string;
  currencyPreference?: string;
  twoFactorEnabled?: boolean;
  isActive?: boolean;
}

const DetailRow = ({ label, value }: { label: string; value: string | boolean | undefined }) => (
  <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
    <dt className="text-sm font-medium text-gray-500">{label}</dt>
    <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
      {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}
    </dd>
  </div>
);

export default function CustomerDetail() {
  const { id } = useParams();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchCustomerData();
    // eslint-disable-next-line
  }, [id]);

  const fetchCustomerData = async () => {
    try {
      const response = await axiosInstance.get(`/customers/${id}`);
      setCustomer(response.data);
    } catch (error) {
      setError('Failed to fetch customer data');
      console.error('Error fetching customer:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 text-red-600 rounded-md">
        {error}
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="text-center text-gray-500">
        Customer not found
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageBreadcrumb pageTitle="Customer Details"  />

      <div className="flex flex-wrap items-center justify-end gap-3">
        <Link
          to={`/customers/${customer.id}`}
          className="inline-flex items-center justify-center rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600"
        >
          Edit Customer
        </Link>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Customer Information</h3>
        </div>
        <div className="border-t border-gray-200">
          <dl>
            <DetailRow label="Full Name" value={customer.fullName} />
            <DetailRow label="Email" value={customer.email} />
            <DetailRow label="Phone Number" value={customer.phoneNumber} />
            <DetailRow label="Profile Picture URL" value={customer.profilePictureUrl} />
            <DetailRow label="Language Preference" value={customer.languagePreference} />
            <DetailRow label="Currency Preference" value={customer.currencyPreference} />
            <DetailRow label="Two Factor Enabled" value={customer.twoFactorEnabled} />
            <DetailRow label="Active" value={customer.isActive} />
          </dl>
        </div>
      </div>
    </div>
  );
} 